import { combineReducers } from 'redux';
import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import psd2Api, { apiCallIds } from '../api';

/**
 * ACTION TYPES
 */
// Institutions & requisitions
export const FETCH_PSD2_INSTITUTIONS = 'psd2/FETCH_PSD2_INSTITUTIONS';
export const CREATE_PSD2_REQUISITION = 'psd2/CREATE_PSD2_REQUISITION';
export const CLEAR_PSD2_REQUISITION_ACCOUNTS = 'psd2/CLEAR_PSD2_REQUISITION_ACCOUNTS';
export const FETCH_PSD2_REQUISITION = 'psd2/FETCH_PSD2_REQUISITION';
export const SET_PSD2_LAST_RESULT = 'psd2/SET_PSD2_LAST_RESULT';

// Account data & balances
export const FETCH_PSD2_IBANS = 'psd2/FETCH_PSD2_IBANS';
export const FETCH_PSD2_ACCOUNT_DATA = 'psd2/FETCH_PSD2_ACCOUNT_DATA';
export const FETCH_PSD2_ACCOUNT_BALANCES = 'psd2/FETCH_PSD2_ACCOUNT_BALANCES';

// Transactions
export const FETCH_PSD2_TX_MONTH = 'psd2/FETCH_PSD2_TX_MONTH';
export const FETCH_PSD2_TX_ALL = 'psd2/FETCH_PSD2_TX_ALL';

// Categories
export const FETCH_PSD2_CATEGORIES = 'psd2/FETCH_PSD2_CATEGORIES';
export const CREATE_PSD2_CATEGORY = 'psd2/CREATE_PSD2_CATEGORY';
export const UPDATE_PSD2_CATEGORY = 'psd2/UPDATE_PSD2_CATEGORY';
export const DELETE_PSD2_CATEGORY = 'psd2/DELETE_PSD2_CATEGORY';

// Aggregation criteria
export const FETCH_PSD2_AGGREGATION_CRITERIA = 'psd2/FETCH_PSD2_AGGREGATION_CRITERIA';
export const CREATE_PSD2_AGGREGATION_CRITERIA = 'psd2/CREATE_PSD2_AGGREGATION_CRITERIA';
export const UPDATE_PSD2_AGGREGATION_CRITERIA = 'psd2/UPDATE_PSD2_AGGREGATION_CRITERIA';
export const DELETE_PSD2_AGGREGATION_CRITERIA = 'psd2/DELETE_PSD2_AGGREGATION_CRITERIA';

// Loan imported accounts
export const FETCH_PSD2_LOAN_IMPORTED_ACCOUNTS = 'psd2/FETCH_PSD2_LOAN_IMPORTED_ACCOUNTS';

// Purge PSD2 data
export const PURGE_PSD2_DATA = 'psd2/PURGE_PSD2_DATA';

/**
 * ACTION CREATORS
 */
export const fetchPsd2InstitutionsActions = createApiActionCreators(FETCH_PSD2_INSTITUTIONS);
export const createPsd2RequisitionActions = createApiActionCreators(CREATE_PSD2_REQUISITION);
export const fetchPsd2RequisitionActions = createApiActionCreators(FETCH_PSD2_REQUISITION);
export const clearPsd2RequisitionAccounts = createActionCreator(CLEAR_PSD2_REQUISITION_ACCOUNTS);
export const setPsd2LastResult = createActionCreator(SET_PSD2_LAST_RESULT);

export const fetchPsd2IbansActions = createApiActionCreators(FETCH_PSD2_IBANS);
export const fetchPsd2AccountDataActions = createApiActionCreators(FETCH_PSD2_ACCOUNT_DATA);
export const fetchPsd2AccountBalancesActions = createApiActionCreators(FETCH_PSD2_ACCOUNT_BALANCES);

export const fetchPsd2TxMonthActions = createApiActionCreators(FETCH_PSD2_TX_MONTH);
export const fetchPsd2AllTxActions = createApiActionCreators(FETCH_PSD2_TX_ALL);

export const fetchPsd2CategoriesActions = createApiActionCreators(FETCH_PSD2_CATEGORIES);
export const createPsd2CategoryActions = createApiActionCreators(CREATE_PSD2_CATEGORY);
export const updatePsd2CategoryActions = createApiActionCreators(UPDATE_PSD2_CATEGORY);
export const deletePsd2CategoryActions = createApiActionCreators(DELETE_PSD2_CATEGORY);

export const fetchPsd2AggregationCriteriaActions = createApiActionCreators(FETCH_PSD2_AGGREGATION_CRITERIA);
export const createPsd2AggregationCriteriaActions = createApiActionCreators(CREATE_PSD2_AGGREGATION_CRITERIA);
export const updatePsd2AggregationCriteriaActions = createApiActionCreators(UPDATE_PSD2_AGGREGATION_CRITERIA);
export const deletePsd2AggregationCriteriaActions = createApiActionCreators(DELETE_PSD2_AGGREGATION_CRITERIA);

// Loan imported accounts
export const fetchPsd2LoanImportedAccountsActions = createApiActionCreators(FETCH_PSD2_LOAN_IMPORTED_ACCOUNTS);

// Purge PSD2 data
export const purgePsd2DataActions = createApiActionCreators(PURGE_PSD2_DATA);

/**
 * REDUCERS
 */
const initial = {
  institutions: [],
  lastResult: 'idle', // 'idle' | 'success' | 'error'

  requisition: null,        // last fetched requisition
  requisitionAccountIds: [],

  ibans: [],                // Array<{ accountId: string, IBAN: string }>
  accountData: null,        // last fetched account-data
  balances: [],             // last fetched balances list

  transactions: [],         // last fetched transactions (all)
  transactionsPage: { pageNumber: 0, pageSize: 0, total: 0 },

  categories: [],
  aggregationCriteria: [],
  loanImportedAccounts: [],
};

const institutions = createReducer(initial.institutions, {
  [FETCH_PSD2_INSTITUTIONS]: {
    [SUCCESS]: (state, payload) => payload || [],
    [FAILURE]: () => [],
  },
});

const lastResult = createReducer(initial.lastResult, {
  [SET_PSD2_LAST_RESULT]: (_state, payload) => payload || 'idle',
  [PURGE_PSD2_DATA]: {
    [REQUEST]: () => 'idle',
    [SUCCESS]: () => 'success',
    [FAILURE]: () => 'error',
  },
});

const requisition = createReducer(initial.requisition, {
  [FETCH_PSD2_REQUISITION]: {
    [SUCCESS]: (_state, payload) => payload || null,
    [FAILURE]: () => null,
  },
});

const requisitionAccountIds = createReducer(initial.requisitionAccountIds, {
  // CLEAR before loading a new loan's requisitions
  [CLEAR_PSD2_REQUISITION_ACCOUNTS]: () => [],

  // MERGE accounts from each requisition fetch
  [FETCH_PSD2_REQUISITION]: {
    [SUCCESS]: (state, payload) => {
      if (!payload) return state || [];
      // normalize to array<string>
      let found = [];
      if (Array.isArray(payload.accounts)) {
        found = payload.accounts;
      }
      // merge + dedupe
      const merged = Array.from(new Set([...(state || []), ...found]));
      return merged;
    },
    [FAILURE]: (state) => state || [],
  },
});

const ibans = createReducer(initial.ibans, {
  [FETCH_PSD2_IBANS]: {
    [SUCCESS]: (_s, payload) => (Array.isArray(payload) ? payload : []),
    [FAILURE]: () => [],
  },
});


const accountData = createReducer(initial.accountData, {
  [FETCH_PSD2_ACCOUNT_DATA]: {
    [SUCCESS]: (_s, payload) => payload || null,
    [FAILURE]: () => null,
  },
});

const balances = createReducer(initial.balances, {
  [FETCH_PSD2_ACCOUNT_BALANCES]: {
    [SUCCESS]: (_s, payload) => payload || [],
    [FAILURE]: () => [],
  },
});

const transactions = createReducer(initial.transactions, {
  [FETCH_PSD2_TX_ALL]: {
    [SUCCESS]: (_s, payload) => (payload && payload.data) ? payload.data : [],
    [FAILURE]: () => [],
  },
  [FETCH_PSD2_TX_MONTH]: {
    [SUCCESS]: (_s, payload) => payload || [],
    [FAILURE]: () => [],
  },
});

const transactionsPage = createReducer(initial.transactionsPage, {
  [FETCH_PSD2_TX_ALL]: {
    [SUCCESS]: (_s, payload) => ({
      pageNumber: payload && typeof payload.pageNumber === 'number' ? payload.pageNumber : 0,
      pageSize: payload && typeof payload.pageSize === 'number' ? payload.pageSize : 0,
      total: payload && typeof payload.total === 'number' ? payload.total : 0,
    }),
    [FAILURE]: () => ({ pageNumber: 0, pageSize: 0, total: 0 }),
  },
});

const categories = createReducer(initial.categories, {
  [FETCH_PSD2_CATEGORIES]: {
    [SUCCESS]: (_s, payload) => payload || [],
    [FAILURE]: () => [],
  },
  [CREATE_PSD2_CATEGORY]: {
    [SUCCESS]: (s, payload) => (payload ? [...s, payload] : s),
  },
  [UPDATE_PSD2_CATEGORY]: {
    [SUCCESS]: (s, payload) => (payload ? s.map(c => (c.id === payload.id ? payload : c)) : s),
  },
  [DELETE_PSD2_CATEGORY]: {
    [SUCCESS]: (s, payload) => s.filter(c => c.id !== payload),
  },
});

const aggregationCriteria = createReducer(initial.aggregationCriteria, {
  [FETCH_PSD2_AGGREGATION_CRITERIA]: {
    [SUCCESS]: (_s, payload) => payload || [],
    [FAILURE]: () => [],
  },
  [CREATE_PSD2_AGGREGATION_CRITERIA]: {
    [SUCCESS]: (s, payload) => (payload ? [...s, payload] : s),
  },
  [UPDATE_PSD2_AGGREGATION_CRITERIA]: {
    [SUCCESS]: (s, payload) => (payload ? s.map(a => (a.id === payload.id ? payload : a)) : s),
  },
  [DELETE_PSD2_AGGREGATION_CRITERIA]: {
    [SUCCESS]: (s, payload) => s.filter(a => a.id !== payload),
  },
});

const loanImportedAccounts = createReducer(initial.loanImportedAccounts, {
  [FETCH_PSD2_LOAN_IMPORTED_ACCOUNTS]: {
    [SUCCESS]: (_s, payload) => (Array.isArray(payload) ? payload : []),
    [FAILURE]: () => [],
  },
});

export default combineReducers({
  institutions,
  lastResult,

  requisition,
  requisitionAccountIds,

  ibans,
  accountData,
  balances,

  transactions,
  transactionsPage,

  categories,
  aggregationCriteria,
  loanImportedAccounts,
});

/**
 * SELECTORS
 */
export const selectPsd2State = (state) => state.psd2 || initial;

export const selectPsd2Institutions = (state) => selectPsd2State(state).institutions;
export const selectPsd2LastResult = (state) => selectPsd2State(state).lastResult;

export const selectPsd2Requisition = (state) => selectPsd2State(state).requisition;
export const selectPsd2RequisitionAccountIds = (state) => selectPsd2State(state).requisitionAccountIds;

export const selectPsd2Ibans = (state) => selectPsd2State(state).ibans;
export const selectPsd2AccountData = (state) => selectPsd2State(state).accountData;
export const selectPsd2Balances = (state) => selectPsd2State(state).balances;

export const selectPsd2Transactions = (state) => selectPsd2State(state).transactions;
export const selectPsd2TransactionsPage = (state) => selectPsd2State(state).transactionsPage;

export const selectPsd2Categories = (state) => selectPsd2State(state).categories;
export const selectPsd2AggregationCriteria = (state) => selectPsd2State(state).aggregationCriteria;
export const selectPsd2LoanImportedAccounts = (state) => selectPsd2State(state).loanImportedAccounts;

// handy helper if you often need a single IBAN
export const selectIbanByAccountId = (state, accountId) => {
  const row = (selectPsd2Ibans(state) || []).find(x => x && x.accountId === accountId);
  return row ? row.IBAN : undefined;
};

/**
 * (Optional) expose for spinner/selectIsInProgress if you decide to use it
 */
export const psd2ApiCallIds = apiCallIds;

/**
 * SAGAS
 */
// Institutions
function* fetchPsd2Institutions() {
  const resp = yield call(psd2Api.fetchInstitutions);
  if (resp && resp.ok) {
    yield put(fetchPsd2InstitutionsActions.success(resp.data));
  } else {
    yield put(fetchPsd2InstitutionsActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Requisitions
function* createPsd2Requisition({ payload }) {
  const resp = yield call(psd2Api.createRequisition, payload);
  if (resp && resp.ok) {
    const data = resp.data || {};
    const link = data.link || data.redirectUrl;
    if (link && typeof window !== 'undefined') {
      window.location.assign(link);
    }
    yield put(createPsd2RequisitionActions.success(data));
  } else {
    yield put(createPsd2RequisitionActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* fetchPsd2Requisition({ payload }) {
  const resp = yield call(psd2Api.getRequisition, payload);
  if (resp && resp.ok) {
    yield put(fetchPsd2RequisitionActions.success(resp.data));
  } else {
    yield put(fetchPsd2RequisitionActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Account data & balances
function* fetchPsd2Ibans({ payload }) {
  // payload should be { ids: string[] } OR directly string[]
  const ids = Array.isArray(payload) ? payload : (payload && payload.ids) ? payload.ids : [];
  const resp = yield call(psd2Api.getAccountIbans, ids); // psd2Api.post('/psd2/iban', { ids })
  if (resp && resp.ok) {
    yield put(fetchPsd2IbansActions.success(resp.data)); // <- keep array {accountId, IBAN}
  } else {
    yield put(fetchPsd2IbansActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* fetchPsd2AccountData({ payload }) {
  const resp = yield call(psd2Api.getAccountData, payload);
  if (resp && resp.ok) {
    yield put(fetchPsd2AccountDataActions.success(resp.data));
  } else {
    yield put(fetchPsd2AccountDataActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* fetchPsd2AccountBalances({ payload }) {
  const resp = yield call(psd2Api.getAccountBalances, payload);
  if (resp && resp.ok) {
    yield put(fetchPsd2AccountBalancesActions.success(resp.data));
  } else {
    yield put(fetchPsd2AccountBalancesActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Transactions
function* fetchPsd2TxMonth({ payload }) {
  // payload: { accountId, year, month }
  const { accountId, year, month } = payload || {};
  const resp = yield call(psd2Api.getAccountTransactionsByMonth, accountId, { year, month });
  if (resp && resp.ok) {
    yield put(fetchPsd2TxMonthActions.success(resp.data));
  } else {
    yield put(fetchPsd2TxMonthActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* fetchPsd2AllTx({ payload }) {
  // payload: { accountId, pageNo?, pageSize? }
  const { accountId, pageNo, pageSize } = payload || {};
  const resp = yield call(psd2Api.getAllAccountTransactions, accountId, { pageNo, pageSize });
  if (resp && resp.ok) {
    yield put(fetchPsd2AllTxActions.success(resp.data));
  } else {
    yield put(fetchPsd2AllTxActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Categories
function* fetchPsd2Categories({ payload }) {
  const resp = yield call(psd2Api.fetchCategories, payload || {});
  if (resp && resp.ok) {
    yield put(fetchPsd2CategoriesActions.success(resp.data));
  } else {
    yield put(fetchPsd2CategoriesActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* createPsd2Category({ payload }) {
  const resp = yield call(psd2Api.createCategory, payload);
  if (resp && resp.ok) {
    yield put(createPsd2CategoryActions.success(resp.data));
    // refresh list
    yield call(fetchPsd2Categories, { payload: {} });
  } else {
    yield put(createPsd2CategoryActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* updatePsd2Category({ payload }) {
  const { id, ...data } = payload || {};
  const resp = yield call(psd2Api.updateCategory, id, data);
  if (resp && resp.ok) {
    yield put(updatePsd2CategoryActions.success(resp.data));
    // refresh list
    yield call(fetchPsd2Categories, { payload: {} });
  } else {
    yield put(updatePsd2CategoryActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* deletePsd2Category({ payload }) {
  const id = payload;
  const resp = yield call(psd2Api.deleteCategory, id);
  if (resp && resp.ok) {
    yield put(deletePsd2CategoryActions.success(id));
    // refresh list
    yield call(fetchPsd2Categories, { payload: {} });
  } else {
    yield put(deletePsd2CategoryActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Aggregation criteria
function* fetchPsd2AggregationCriteria({ payload }) {
  const resp = yield call(psd2Api.fetchAggregationCriteria, payload || {});
  if (resp && resp.ok) {
    yield put(fetchPsd2AggregationCriteriaActions.success(resp.data));
  } else {
    yield put(fetchPsd2AggregationCriteriaActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* createPsd2AggregationCriteria({ payload }) {
  const resp = yield call(psd2Api.createAggregationCriteria, payload);
  if (resp && resp.ok) {
    yield put(createPsd2AggregationCriteriaActions.success(resp.data));
    // refresh
    yield call(fetchPsd2AggregationCriteria, { payload: {} });
  } else {
    yield put(createPsd2AggregationCriteriaActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* updatePsd2AggregationCriteria({ payload }) {
  const { id, ...data } = payload || {};
  const resp = yield call(psd2Api.updateAggregationCriteria, id, data);
  if (resp && resp.ok) {
    yield put(updatePsd2AggregationCriteriaActions.success(resp.data));
    // refresh
    yield call(fetchPsd2AggregationCriteria, { payload: {} });
  } else {
    yield put(updatePsd2AggregationCriteriaActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

function* deletePsd2AggregationCriteria({ payload }) {
  const id = payload;
  const resp = yield call(psd2Api.deleteAggregationCriteria, id);
  if (resp && resp.ok) {
    yield put(deletePsd2AggregationCriteriaActions.success(id));
    // refresh
    yield call(fetchPsd2AggregationCriteria, { payload: {} });
  } else {
    yield put(deletePsd2AggregationCriteriaActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Loan imported accounts
function* fetchPsd2LoanImportedAccounts({ payload }) {
  const loanId = payload;
  const resp = yield call(psd2Api.getLoanImportedAccounts, loanId);
  if (resp && resp.ok) {
    yield put(fetchPsd2LoanImportedAccountsActions.success(resp.data));
  } else {
    yield put(fetchPsd2LoanImportedAccountsActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

// Purge PSD2 data
function* purgePsd2Data({ payload }) {
  // payload: { requisitionIds: string[] }
  const resp = yield call(psd2Api.purgeAccountData, payload || {});
  if (resp && resp.ok) {
    yield put(purgePsd2DataActions.success(resp.data));
  } else {
    yield put(purgePsd2DataActions.failure(resp ? resp.error : 'Unknown error'));
  }
}

export function* psd2Saga() {
  // Institutions & requisitions
  yield takeLatest(createActionType(FETCH_PSD2_INSTITUTIONS, REQUEST), fetchPsd2Institutions);
  yield takeLatest(createActionType(CREATE_PSD2_REQUISITION, REQUEST), createPsd2Requisition);
  yield takeEvery(createActionType(FETCH_PSD2_REQUISITION, REQUEST), fetchPsd2Requisition);

  // Account data & balances
  yield takeLatest(createActionType(FETCH_PSD2_IBANS, REQUEST), fetchPsd2Ibans);
  yield takeLatest(createActionType(FETCH_PSD2_ACCOUNT_DATA, REQUEST), fetchPsd2AccountData);
  yield takeLatest(createActionType(FETCH_PSD2_ACCOUNT_BALANCES, REQUEST), fetchPsd2AccountBalances);

  // Transactions
  yield takeLatest(createActionType(FETCH_PSD2_TX_MONTH, REQUEST), fetchPsd2TxMonth);
  yield takeLatest(createActionType(FETCH_PSD2_TX_ALL, REQUEST), fetchPsd2AllTx);

  // Categories
  yield takeLatest(createActionType(FETCH_PSD2_CATEGORIES, REQUEST), fetchPsd2Categories);
  yield takeLatest(createActionType(CREATE_PSD2_CATEGORY, REQUEST), createPsd2Category);
  yield takeLatest(createActionType(UPDATE_PSD2_CATEGORY, REQUEST), updatePsd2Category);
  yield takeLatest(createActionType(DELETE_PSD2_CATEGORY, REQUEST), deletePsd2Category);

  // Aggregation criteria
  yield takeLatest(createActionType(FETCH_PSD2_AGGREGATION_CRITERIA, REQUEST), fetchPsd2AggregationCriteria);
  yield takeLatest(createActionType(CREATE_PSD2_AGGREGATION_CRITERIA, REQUEST), createPsd2AggregationCriteria);
  yield takeLatest(createActionType(UPDATE_PSD2_AGGREGATION_CRITERIA, REQUEST), updatePsd2AggregationCriteria);
  yield takeLatest(createActionType(DELETE_PSD2_AGGREGATION_CRITERIA, REQUEST), deletePsd2AggregationCriteria);

  // Loan imported accounts
  yield takeLatest(createActionType(FETCH_PSD2_LOAN_IMPORTED_ACCOUNTS, REQUEST), fetchPsd2LoanImportedAccounts);

  // Purge PSD2 data
  yield takeLatest(createActionType(PURGE_PSD2_DATA, REQUEST), purgePsd2Data);
}



// WEBPACK FOOTER //
// ./src/features/psd2/ducks/index.js